import React, { useState, useEffect } from 'react';
import { useCustomInvitations } from '../../../firebase/api/invitations';
import { usePrivateBoards } from '../../../firebase/api/boards-private';
import moment from 'moment';
import preloaderImg from '../../../svg/dot-preloader-dark.svg';
import MonthStatistic from './MonthStatistic';
import DayStatistic from './DayStatistic';
import { getMonthReport } from '../../../firebase/api/tracking';
import styled from 'styled-components';

const DayTitle = styled.div`
  text-align: center;
  font-size: 30px;
  color: #000;
`;

const SImg = styled.img`
  display: block;
  width: 30px;
  margin: 40px auto;
`;

const Title = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #999;
  span {
    cursor: pointer;
    &.active {
      font-size: 30px;
      color: #000;
      cursor: default;
    }
  }
`;

export const months = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const incrementMonth = (month: number) => (month === 11 ? 0 : month + 1);
const decrementMonth = (month: number) => (month === 0 ? 11 : month - 1);
const incrementYear = (year: number) => year + 1;
const decrementYear = (year: number) => year - 1;

export interface IReport {
  day: string;
  tasks: { id: string; seconds: number; boardId: string }[];
}

const UserStatistic: React.FC<{ currentUserId: string }> = (props) => {
  const { boards } = usePrivateBoards();
  const boardIds = boards.map((b) => b.id);
  const { invitations: dataInvitations, loading: invitationsLoading } = useCustomInvitations(
    props.currentUserId
  );
  const invitations = dataInvitations.filter((inv) => boardIds.includes(inv.id));
  const [report, setMonthReport] = useState<IReport[]>([]);
  const monthReport = report.map((el) => ({
    ...el,
    tasks: el.tasks.filter((t) => boardIds.includes(t.boardId)),
  }));
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const [day, setDay] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const yearMonth = `${month}-${year}`;

  const getStatistic = async () => {
    try {
      setLoading(true);
      const res = await getMonthReport(props.currentUserId, yearMonth);
      const report =
        res &&
        Object.keys(res).map((day) => ({
          day,
          tasks: Object.keys(res[day]).map((id) => ({
            id,
            seconds: res[day][id].seconds,
            boardId: res[day][id].boardId || 0,
          })),
        }));
      setLoading(false);
      report && void setMonthReport(report);
    } catch (e) {
      setLoading(false);
      console.log(e);
      throw new Error();
    }
  };

  useEffect(() => {
    getStatistic();
  }, [props.currentUserId, month]);

  useEffect(() => {
    setDay(0);
    setMonthReport([]);
  }, [props.currentUserId]);

  return (
    <>
      {!!day ? (
        <DayTitle>{moment(day).format('Do MMMM YYYY')}</DayTitle>
      ) : (
        <Title>
          <span
            onClick={() => {
              setMonthReport([]);
              if (month === 0) {
                setMonth(decrementMonth(month));
                setYear(decrementYear(year));
              } else {
                setMonth(decrementMonth(month));
              }
            }}
          >
            ← {months[decrementMonth(month)]}
          </span>
          <span className="active">
            {months[month]} {year}
          </span>
          <span
            onClick={() => {
              setMonthReport([]);
              if (month === 11) {
                setMonth(incrementMonth(month));
                setYear(incrementYear(year));
              } else {
                setMonth(incrementMonth(month));
              }
            }}
          >
            {months[incrementMonth(month)]} →
          </span>
        </Title>
      )}
      {day ? (
        <DayStatistic
          currentUserId={props.currentUserId}
          invitations={invitations}
          day={day}
          resetDay={() => setDay(0)}
          loading={invitationsLoading}
        />
      ) : (
        <>
          {loading ? (
            <SImg src={preloaderImg} alt="loading..." />
          ) : (
            <MonthStatistic
              daysStatistic={monthReport}
              year={year}
              month={month}
              setDay={(day: number) => setDay(day)}
            />
          )}
        </>
      )}
    </>
  );
};

export default UserStatistic;
